import lss from '@/assets/configurations/localStorageStructure'
import { UserSettings } from '@/assets/configurations/avalableItems.json'
const { interfaceLanguages = [] } = UserSettings
const DEFAULT_LANG = 'sl_SI'
import Storage from 'dom-storage'

export { DEFAULT_LANG }

export default {
  /**
   * Helper to determine localStorage. Mainly for test runner.
   * @returns {Storage}
   */
  getLS () {
    return window.localStorage || global.localStorage || new Storage(null, { strict: true })
  },

  /**
   * Gets the qwilink data block
   * @returns {Object} Qwilink data block
   */
  getQwilinkBlock () {
    let ls = {}
    for (let lsi in lss) {
      if (lsi in this.getLS()) {
        ls[lsi] = Object.assign({}, lss[lsi], JSON.parse(this.getLS().getItem(lsi)))
      }
    }

    return ls
  },

  /**
   * Stores the given data block to localStorage
   * @param {Object} qwilinkBlock Qwilink data block to store
   */
  storeQwilinkBlock (qwilinkBlock) {
    if (!qwilinkBlock) return false

    for (let k in qwilinkBlock) {
      let val = qwilinkBlock[k]
      if (typeof qwilinkBlock[k] == 'object') {
        val = JSON.stringify(qwilinkBlock[k])
      }

      this.getLS().setItem(k, val)
    }

    return true
  },

  /**
   * Checks if qwilink data block is set
   * @returns {Boolean} If the structure is set
   */
  qwilinkBlockSet () {
    return 'qwilink' in this.getLS()
  },

  /**
   * Checks localStructure
   * @returns {Boolean} If the structure is valid
   */
  checkStructure () {
    if (!this.qwilinkBlockSet()) return false

    let expected = lss
    let actual = this.getQwilinkBlock()

    // All keys must be present
    let k1 = Object.keys(expected)
    let k2 = Object.keys(actual)  
    if (k1.length !== k2.length) return false

    /**
     * @see getQwilinkBlock() filters out objects, that are not used by
     * qwilink, so if lengths don't match we know structure is not valid
     */

    return true
  },

  /**
   * Reinits localStorage
   * @param {Object} baseStructure The structure to use
   */
  reinitStructure (baseStructure) {
    // Reinit Qwilinks localStorage...
    return this.storeQwilinkBlock(baseStructure)
  },

  /**
   * Gets data from localStorage
   * @param {String} set The set the data is from
   * @param {String} key The key from that set
   */
  getData (set, key = undefined) {
    if (!this.checkStructure()) return undefined
    let block = this.getQwilinkBlock()

    let searchedSet = block
    if (set.constructor !== Array) {
      set = [set]
    }

    for (let s of set) {
      if (!(s in searchedSet)) return undefined
      searchedSet = searchedSet[s]
    }

    if (key === undefined) {
      return searchedSet
    }
    return searchedSet[key]
  },

  /**
   * Sets the data to localStorage and overwrites existing data on that key
   * @param {String} set The set the data is from
   * @param {String} key The key from that data set
   * @param {*} value The value to use
   */
  setData (set, key, value) {
    if (!this.checkStructure()) return undefined
    let block = this.getQwilinkBlock()

    let searchedSet = block
    if (set.constructor !== Array) {
      set = [set]
    }

    for (let s of set) {
      if (!(s in searchedSet)) return false
      searchedSet = searchedSet[s]
    }

    if (!(key in searchedSet)) return false

    searchedSet[key] = value
    this.storeQwilinkBlock(block)
    return true
  },

  /**
   * Updates storage's set with some data
   * @param {String} set Set's name
   * @param {*} value Data
   */
  setBlock (set, value) {
    if (!this.checkStructure()) return undefined

    let block = this.getQwilinkBlock()
    if (!(set in lss)) return false

    block[set] = value
    this.storeQwilinkBlock(block)
    return true
  },

  /**
   * Adds data to localStorage
   * @param {String} set The set the data is from
   * @param {String} key The key from that data set
   * @param {*} value The value to use
   * @param {*} valueKey The value key to use - if any at all
   */
  addData (set, key, value, valueKey = false) {
    if (!this.checkStructure()) return undefined

    let data = this.getData(set, key)
    if (typeof(data) != 'object' || data === null) return false

    if (valueKey) {
      data[valueKey] = value
    } else {
      data.push(value)
    }

    return this.setData(set, key, data)
  },

  /**
   * Prepares localStorage for impersonation by moving user info around
   * @param {Object} impersonatedUser
   */
  startImpersonating (impersonatedUser) {
    if (!this.checkStructure()) return false
    if (this.getData('impersonation', 'impersonating')) return false

    // Ditch reference
    let user = JSON.parse(JSON.stringify(this.getData('user')))
    this.setData('impersonation', 'impersonating', true)
    this.setBlock('altUser', user)
    this.setBlock('user', impersonatedUser)
    return true
  },

  /**
   * Prepares localStorage for impersonation by moving user info around
   */
  stopImpersonating () {
    if (!this.checkStructure()) return false
    if (!this.getData('impersonation', 'impersonating')) return false

    // Ditch reference
    let user = JSON.parse(JSON.stringify(this.getData('altUser')))
    this.setData('impersonation', 'impersonating', false)
    this.setBlock('altUser', lss.altUser)
    this.setBlock('user', user)
    return true
  },

  /**
   * Receives a response object and stores it to localStorage
   * @param {Object} request The request's response from API
   */
  logRequest (request, set) {
    if (!this.checkStructure()) return undefined

    let requests = this.getData('apiRequests', set)
    if (requests === null) requests = []

    if (requests.length >= 5) {
      requests.shift()
    }

    requests.push(request)
    this.setData('apiRequests', set, requests)
    return true
  },

  /**
   * Returns a list of previous requests
   * @returns {Array} An array or requests
   */
  getRequests (set) {
    return this.getData('apiRequests', set) || []
  },

  /**
   * Returns user's language; shortened and longer versions
   * @returns {Object}
   */
  getLocale () {
    let langs = (this.getData('interface', 'language') || DEFAULT_LANG)
    if (!interfaceLanguages.find(l => l.value === langs)) {
      langs = DEFAULT_LANG
    }
    langs = langs.split('_')

    return {
      short: langs[0],
      long: langs.join('_')
    }
  }
}



// WEBPACK FOOTER //
// ./src/assets/modules/localStorageHelper.js